// Implements: F-VER-004, F-VER-006, F-CNV-015, F-UIX-007, F-UIX-009, F-UIX-015, F-PRV-017
/**
 * One version: its metadata, its pieces, the way to make it current and the
 * downloads. Pure presentation.
 *
 * The current version is pointed out with TEXT (a `Badge` that says it), as in
 * the tree, never with colour alone. The downloads are buttons, not links: the
 * container fetches the file through the client so a refusal is a notice and
 * not an error page saved as the file. A piece of a kind the dictionary has no
 * word for
 * keeps its own name instead of disappearing.
 *
 * A version with a diagram shows it drawn, in the application's one sandboxed
 * frame, above the downloads (F-PRV-017): the person sees what they are about
 * to save. A version without one shows no preview at all.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { SandboxedFrame } from "../../../shared/ui/atoms/SandboxedFrame";
import { byteText } from "../../../shared/ui/byteText";
import { dayText } from "../../../shared/ui/dayText";
import { previewDiagramDocumentPath } from "../../../shared/ui/frameDocumentPath";
import type { VersionDetailView } from "../../../types/api";
import { kindKey } from "./artifactKinds";
import styles from "./VersionDetail.module.css";

export type DownloadKey = "html" | "zip" | "diagram";

const DOWNLOAD_KEYS_ORDER: readonly DownloadKey[] = ["html", "zip", "diagram"];

export interface VersionDetailProps {
  readonly version: VersionDetailView;
  readonly isCurrent: boolean;
  readonly promoting: boolean;
  readonly onPromote: () => void;
  /** Only an owner or an editor may move the pointer: promote and continue are hidden otherwise. */
  readonly canEdit: boolean;
  readonly continuing: boolean;
  readonly onContinue: () => void;
  /** The file being fetched now, so the same one cannot be asked for twice. */
  readonly downloading: DownloadKey | null;
  readonly onDownload: (key: DownloadKey) => void;
}


const DOWNLOAD_KEYS: Readonly<
  Record<
    DownloadKey,
    "version.download.html" | "version.download.zip" | "version.download.diagram"
  >
> = {
  html: "version.download.html",
  zip: "version.download.zip",
  diagram: "version.download.diagram",
};

export function VersionDetail({
  version,
  isCurrent,
  promoting,
  onPromote,
  canEdit,
  continuing,
  onContinue,
  downloading,
  onDownload,
}: VersionDetailProps): ReactElement {
  const { t } = useTranslation();
  const hasDiagram = version.artifacts.some((piece) => piece.kind === "diagram");

  return (
    <div className={styles.detail}>
      <div className={styles.headline}>
        <h2 className={styles.title}>{t("version.heading", { n: version.sequence_number })}</h2>
        {isCurrent ? <Badge tone="info">{t("versions.current")}</Badge> : null}
      </div>
      <dl className={styles.facts}>
        <dt>{t("version.flow")}</dt>
        <dd>{version.flow_version}</dd>
        <dt>{t("version.created")}</dt>
        <dd>
          <time dateTime={version.created_at}>{dayText(version.created_at)}</time>
        </dd>
        <dt>{t("version.note")}</dt>
        <dd>{version.note ?? t("version.noNote")}</dd>
      </dl>

      <ul className={styles.pieces} aria-label={t("version.artifacts")}>
        {version.artifacts.map((piece) => {
          const key = kindKey(piece.kind);
          return (
            <li key={piece.kind}>
              {key === undefined ? piece.kind : t(key)}
              {" · "}
              {byteText(piece.byte_size)}
            </li>
          );
        })}
      </ul>

      {isCurrent || !canEdit ? null : (
        <Button variant="secondary" loading={promoting} disabled={continuing} onClick={onPromote}>
          {t("version.promote")}
        </Button>
      )}
      {isCurrent || !canEdit ? null : (
        <Button variant="secondary" loading={continuing} disabled={promoting} onClick={onContinue}>
          {t("version.continue")}
        </Button>
      )}

      {hasDiagram ? (
        <SandboxedFrame
          key={version.id}
          className={styles.diagram}
          title={t("version.diagramPreview")}
          src={previewDiagramDocumentPath(version.mockup_id, version.id)}
        />
      ) : null}

      <nav className={styles.downloads} aria-label={t("version.download.title")}>
        {DOWNLOAD_KEYS_ORDER.map((key) => (
          <Button
            key={key}
            variant="ghost"
            size="sm"
            loading={downloading === key}
            onClick={() => {
              onDownload(key);
            }}
          >
            {t(DOWNLOAD_KEYS[key])}
          </Button>
        ))}
      </nav>
    </div>
  );
}
